import type { ChapterData } from "../types/chapter";
import type { GameSave } from "../types/save";

export interface LevelCompletionRecord {
  readonly levelId: string;
  readonly chapterId: string;
  readonly cleared: boolean;
  readonly perfected: boolean;
  readonly collectedIds: readonly string[];
  readonly completedGroups?: readonly string[];
  readonly triggeredBreadcrumbIds?: readonly string[];
}

export interface ProgressionUpdate {
  readonly save: GameSave;
  readonly newlyUnlockedLevelIds: readonly string[];
  readonly newlyUnlockedChapterIds: readonly string[];
}

export function applyLevelCompletion(
  save: GameSave,
  completion: LevelCompletionRecord,
  chapter: ChapterData,
): ProgressionUpdate {
  const newlyUnlockedLevelIds: string[] = [];
  const newlyUnlockedChapterIds: string[] = [];

  let progression = save.progression;
  let collectibles = save.collectibles;
  let fog = save.fog;
  const cleared = completion.cleared;
  const perfected = cleared && completion.perfected;

  let triggeredBreadcrumbs = save.triggeredBreadcrumbs;

  if (cleared) {
    if (!progression.clearedLevels.includes(completion.levelId)) {
      progression = {
        ...progression,
        clearedLevels: [...progression.clearedLevels, completion.levelId],
      };
    }

    const groupsFromCompletion = completion.completedGroups ?? [];
    const existingGroups = collectibles.completedGroupsByLevel?.[completion.levelId] ?? [];
    const mergedGroups = [...new Set([...existingGroups, ...groupsFromCompletion])];
    if (
      mergedGroups.length !== existingGroups.length ||
      !mergedGroups.every((g) => existingGroups.includes(g))
    ) {
      collectibles = {
        ...collectibles,
        completedGroupsByLevel: {
          ...collectibles.completedGroupsByLevel,
          [completion.levelId]: mergedGroups,
        },
      };
    }

    if (completion.triggeredBreadcrumbIds && completion.triggeredBreadcrumbIds.length > 0) {
      const existing = new Set(triggeredBreadcrumbs);
      const merged = [...triggeredBreadcrumbs];
      for (const id of completion.triggeredBreadcrumbIds) {
        if (!existing.has(id)) {
          merged.push(id);
        }
      }
      triggeredBreadcrumbs = merged;
    }
  }

  if (perfected) {
    if (!progression.perfectedLevels.includes(completion.levelId)) {
      progression = {
        ...progression,
        perfectedLevels: [...progression.perfectedLevels, completion.levelId],
      };
    }

    const existingCollectibles = collectibles.byLevel[completion.levelId] ?? [];
    const mergedCollectibles = [
      ...new Set([...existingCollectibles, ...completion.collectedIds]),
    ];
    if (
      mergedCollectibles.length !== existingCollectibles.length ||
      !mergedCollectibles.every((id) => existingCollectibles.includes(id))
    ) {
      collectibles = {
        ...collectibles,
        byLevel: {
          ...collectibles.byLevel,
          [completion.levelId]: mergedCollectibles,
        },
      };
    }

    if (!collectibles.firstDiscoveredTypes.includes(chapter.collectibleType)) {
      collectibles = {
        ...collectibles,
        firstDiscoveredTypes: [
          ...collectibles.firstDiscoveredTypes,
          chapter.collectibleType,
        ],
      };
    }

    if (!fog.permanentRevealLevels.includes(completion.levelId)) {
      fog = {
        ...fog,
        permanentRevealLevels: [...fog.permanentRevealLevels, completion.levelId],
      };
    }
  }

  const levelIndex = chapter.levels.indexOf(completion.levelId);
  if (cleared && levelIndex >= 0 && levelIndex < chapter.levels.length - 1) {
    const nextLevelId = chapter.levels[levelIndex + 1];
    if (nextLevelId && !progression.unlockedLevels.includes(nextLevelId)) {
      progression = {
        ...progression,
        unlockedLevels: [...progression.unlockedLevels, nextLevelId],
      };
      newlyUnlockedLevelIds.push(nextLevelId);
    }
  }

  return {
    save: {
      ...save,
      progression,
      collectibles,
      fog,
      triggeredBreadcrumbs,
    },
    newlyUnlockedLevelIds,
    newlyUnlockedChapterIds,
  };
}

export function applyLockPass(
  save: GameSave,
  chapter: ChapterData,
): ProgressionUpdate {
  const newlyUnlockedLevelIds: string[] = [];
  const newlyUnlockedChapterIds: string[] = [];

  let progression = save.progression;

  const lockNode = chapter.nodes.find((n) => n.type === "lock");
  const lockId = lockNode?.id ?? chapter.id;

  if (!progression.passedLocks.includes(lockId)) {
    progression = {
      ...progression,
      passedLocks: [...progression.passedLocks, lockId],
    };
  }

  if (!progression.unlockedChapters.includes(chapter.id)) {
    progression = {
      ...progression,
      unlockedChapters: [...progression.unlockedChapters, chapter.id],
    };
    newlyUnlockedChapterIds.push(chapter.id);
  }

  if (chapter.kind === "ending" && !progression.endUnlocked) {
    progression = {
      ...progression,
      endUnlocked: true,
    };
  }

  const firstLevelId = chapter.levels[0];
  if (firstLevelId && !progression.unlockedLevels.includes(firstLevelId)) {
    progression = {
      ...progression,
      unlockedLevels: [...progression.unlockedLevels, firstLevelId],
    };
    newlyUnlockedLevelIds.push(firstLevelId);
  }

  return {
    save: {
      ...save,
      progression,
    },
    newlyUnlockedLevelIds,
    newlyUnlockedChapterIds,
  };
}
